import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingProvider, TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers/tracking-provider';
import { Store } from 'vuex';

import { NotificationSubscriptionType } from '@/stores/braze.store';

const BRAZE_ALLOWED_EVENTS = {
	app_open: true,
	app_install: true,
	clickout: true,
	dislikelist_add: true,
	dislikelist_remove: true,
	likelist_add: true,
	likelist_remove: true,
	morelist_add: true,
	morelist_remove: true,
	pushnotification: ['push_granted'],
	search_suggest_click: true,
	seenlist_add: true,
	seenlist_remove: true,
	share_content: true,
	taste_onboarding: ['survey_completed'],
	user_account: ['account_login', 'account_signup', 'provider_clicked'],
	watchlist_add: true,
	userinteraction: [
		'enter_tv_code_click',
		'go_pro_clicked',
		'pro_feedback_survey_seen',
		'pro_feedback_submitted',
		'pro_feedback_dismissed',
	],
	watchlist_remove: true,
	youtube_started: true,
	play_on_tv: ['pot_button_pressed', 'device_selected_roku', 'device_selected_googlecast', 'device_selected_webhook'],
};

export class TrackingProviderBraze extends TrackingProvider {
	dataProcessingService = null;

	private braze: AppboyWebPlugin | null;
	constructor(private config: { store: Store<any>; isPushClicked?: boolean }) {
		super();
		this.braze = null;
	}

	changeUser(jwLoginId: string) {
		if (!this.braze) return;
		return this.braze.changeUser(jwLoginId);
	}

	getUser() {
		if (!this.braze) return null;
		return (this.braze as AppboyWebPlugin).getUser();
	}

	initialize() {
		return new Promise<void>(async (resolve, reject) => {
			await this.initSdk();

			if (!this.braze) {
				return;
			}

			const { store, isPushClicked } = this.config;
			const { jwId, jwLoginId } = store.state.user;

			try {
				(this.braze as AppboyWebPlugin)?.initialize(JW_CONFIG.BRAZE_WEB_API_KEY, {
					baseUrl: JW_CONFIG.BRAZE_BASE_URL,
					safariWebsitePushId: 'web.com.justwatch',
				});

				if (isPushClicked) {
					(this.braze as AppboyWebPlugin)?.registerAppboyPushMessages();
				}

				(this.braze as AppboyWebPlugin)?.openSession();

				// Braze cold start
				const {
					aliasId,
					email: brazeStoreEmail,
					externalId,
					name: brazeStoreName,
					notificationType,
				} = store.state.braze;

				// Set jw_id custom attribute
				this.getUser()?.setCustomUserAttribute('jwId', jwId);

				if (jwLoginId && !externalId) {
					// user logged in
					this.changeUser(jwLoginId);

					store.dispatch('braze/setExternalId', jwLoginId);

					const { info } = store.state.user;
					const { email, name } = info;

					if (email && !brazeStoreEmail) {
						this.getUser()?.setEmail(email);
						store.dispatch('braze/setEmail', email);
					}
					if (name && !brazeStoreName) {
						this.getUser()?.setFirstName(name);
						store.dispatch('braze/setFirstName', name);
					}
				} else if (jwId && !aliasId) {
					// user with only jwId
					this.getUser()?.addAlias(jwId, 'jwId');
					store.dispatch('braze/setAliasJwId', jwId);
				}

				if (!notificationType) {
					this.getUser()?.setPushNotificationSubscriptionType(NotificationSubscriptionType.OPTED_IN);
					store.dispatch('braze/setNotificationSubscriptionType', NotificationSubscriptionType.OPTED_IN);
				}
			} catch (err) {
				console.error('[Braze] init issue', err);
			}

			// Watch country changes
			store.watch(
				(state: any) => state.language.webLocale,
				async () => {
					const country = store.getters['language/country'];
					this.getUser()?.setCountry(country);
					store.dispatch('braze/setCountry', country);
				}
			);

			// Watch language changes
			store.watch(
				(state: any) => state.language.overriddenLanguage,
				async () => {
					const language = store.getters['language/language'];
					this.getUser()?.setLanguage(language);
					store.dispatch('braze/setLanguage', language);
				}
			);

			// Watch login changes
			store.watch(
				(state: any) => state.user.jwLoginId,
				async (jwLoginId: string, oldJwLoginId: string) => {
					if (jwLoginId) {
						const { info } = store.state.user;
						const { email, name } = info;

						this.changeUser(jwLoginId);
						store.dispatch('braze/setExternalId', jwLoginId);

						this.getUser()?.setPushNotificationSubscriptionType(NotificationSubscriptionType.OPTED_IN);
						store.dispatch('braze/setNotificationSubscriptionType', NotificationSubscriptionType.OPTED_IN);

						if (email) {
							this.getUser()?.setEmail(email);
							store.dispatch('braze/setEmail', email);
						}
						if (name) {
							this.getUser()?.setFirstName(name);
							store.dispatch('braze/setFirstName', name);
						}
					} else {
						// handle logout
						// For reference see  #https://www.braze.com/docs/developer_guide/platform_integration_guides/ios/analytics/setting_user_ids/

						// reset braze store values (unless product decide otherwise)
						store.dispatch('braze/setExternalId', undefined);
						store.dispatch('braze/setEmail', undefined);
						store.dispatch('braze/setFirstName', undefined);
						store.dispatch('braze/setPreviousExternalId', oldJwLoginId);
					}
				}
			);

			resolve();
		});
	}

	async initSdk() {
		// TEMP disable Braze for Web because we aren't using it much but paying for each extra user
		return;
	}

	async trackPage({ path, title }: { path?: string; title?: string }) {}

	async trackEvent(category: string, properties: TrackingProviderPropertiesInterface, contexts: SnowplowContext[]) {
		const key = category as keyof typeof BRAZE_ALLOWED_EVENTS;
		const event = BRAZE_ALLOWED_EVENTS[key];

		let isAllowed = Array.isArray(event) ? properties.action && event.includes(properties.action) : event;

		if (isAllowed) {
			this.braze?.logCustomEvent(category, properties);
		}
	}
}
